import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, StatusBadge, Modal, FormField, TextInput, Select } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { TwoPane } from '@/components/layout/TwoPane';
import { toast } from '@/components/ui/Toast';
import {
  type TicketApi, type TicketSummary, type TicketDetail,
  STATUS_LABEL, STATUS_TONE, PRIORITY_LABEL, PRIORITY_TONE,
} from './types';

interface Props {
  api: TicketApi;
  title: string;
  // Which audiences this persona may open. Empty = cannot open new tickets.
  canOpen?: { value: 'internal' | 'platform'; label: string }[];
  // Whether this persona is the support side (can change status/priority).
  canManage?: boolean;
}

export function TicketsPage({ api, title, canOpen = [], canManage = false }: Props) {
  const [list, setList] = useState<TicketSummary[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [active, setActive] = useState<TicketDetail | null>(null);
  const [reply, setReply] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.list(statusFilter || undefined);
      setList(res.data);
    } catch {
      toast('שגיאה בטעינת הפניות', 'danger');
    }
  }, [api, statusFilter]);

  useEffect(() => { void load(); }, [load]);

  async function open(t: TicketSummary) {
    try {
      const d = await api.get(t.id);
      setActive(d);
      await api.markRead(t.id);
      void load();
    } catch {
      toast('שגיאה בטעינת הפנייה', 'danger');
    }
  }

  async function send() {
    if (!active || (!reply.trim() && files.length === 0)) return;
    setSending(true);
    try {
      const d = await api.reply(active.id, reply, files);
      setActive(d);
      setReply('');
      setFiles([]);
      void load();
    } catch {
      toast('שגיאה בשליחה', 'danger');
    } finally {
      setSending(false);
    }
  }

  async function changeStatus(status: string) {
    if (!active || !api.update) return;
    try {
      await api.update(active.id, { status });
      setActive({ ...active, status });
      void load();
    } catch {
      toast('שגיאה בעדכון סטטוס', 'danger');
    }
  }

  const listPane = (
      <div className="stack" style={{ minHeight: 0, height: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--t-lg)' }}>{title}</h2>
          {canOpen.length > 0 && (
            <button type="button" className="btn primary" onClick={() => setNewOpen(true)}>+ פנייה</button>
          )}
        </div>
        <Select value={statusFilter} onChange={setStatusFilter} options={[
          { value: '', label: 'כל הסטטוסים' },
          ...Object.entries(STATUS_LABEL).map(([v, l]) => ({ value: v, label: l })),
        ]} />
        <Card padded={false} style={{ overflowY: 'auto', flex: 1 }}>
          {list.length === 0 ? (
            <div style={{ padding: 'var(--pad-card)', color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין פניות.</div>
          ) : list.map((t) => (
            <button key={t.id} type="button" onClick={() => open(t)}
              style={{
                width: '100%', textAlign: 'start', padding: 'var(--pad-row) var(--pad-card)',
                background: active?.id === t.id ? 'var(--accent-soft)' : 'transparent',
                border: 'none', borderBottom: '1px solid var(--line)', cursor: 'pointer', fontFamily: 'inherit',
              }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                <strong style={{ fontSize: 'var(--t-sm)' }}>{t.subject}</strong>
                <StatusBadge tone={STATUS_TONE[t.status] ?? 'neutral'} size="sm">{STATUS_LABEL[t.status] ?? t.status}</StatusBadge>
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center', fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                <StatusBadge tone={PRIORITY_TONE[t.priority] ?? 'neutral'} size="sm">{PRIORITY_LABEL[t.priority] ?? t.priority}</StatusBadge>
                <span>{t.opener_name ?? ''}</span>
                {t.last_message_at && <span>· {new Date(t.last_message_at).toLocaleDateString('he-IL')}</span>}
              </div>
            </button>
          ))}
        </Card>
      </div>
  );

  const threadPane = (
      <Card padded={false} fill style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
        {!active ? (
          <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: 'var(--muted)' }}>בחר פנייה</div>
        ) : (
          <>
            <div style={{ padding: 'var(--pad-card)', borderBottom: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <div>
                <strong>{active.subject}</strong>
                <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                  {active.opener_name} · {PRIORITY_LABEL[active.priority] ?? active.priority}
                </div>
              </div>
              {canManage && api.update ? (
                <select value={active.status} onChange={(e) => changeStatus(e.target.value)} style={selectCss}>
                  {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              ) : (
                <StatusBadge tone={STATUS_TONE[active.status] ?? 'neutral'} size="sm">{STATUS_LABEL[active.status] ?? active.status}</StatusBadge>
              )}
            </div>

            <div style={{ flex: 1, minHeight: 0, padding: 'var(--pad-card)', overflowY: 'auto' }}>
              {active.messages.map((m) => (
                <div key={m.id} style={{ display: 'flex', justifyContent: m.is_support ? 'flex-start' : 'flex-end', marginBottom: 10 }}>
                  <div style={{
                    maxWidth: '75%', padding: 'var(--pad-row) var(--pad-card)', borderRadius: 'var(--r-2)',
                    background: m.is_support ? 'var(--surface-2)' : 'var(--accent-soft)',
                  }}>
                    <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginBottom: 2 }}>
                      {m.author_name} {m.is_support ? '· תמיכה' : ''} · {new Date(m.created_at).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' })}
                    </div>
                    <div style={{ fontSize: 'var(--t-sm)', whiteSpace: 'pre-wrap' }}>{m.body}</div>
                    {m.attachments.map((a) => (
                      <a key={a.id} href={api.attachmentUrl(a.id)} target="_blank" rel="noreferrer"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 6, fontSize: 'var(--t-xs)', color: 'var(--accent-ink)' }}>
                        <Icon name="doc" size={12} /> {a.name}
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8, padding: 'var(--pad-card)', borderTop: '1px solid var(--line)', alignItems: 'center', flexShrink: 0, background: 'var(--surface)' }}>
              <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={(e) => setFiles(Array.from(e.target.files ?? []))} />
              <button type="button" className="btn ghost" onClick={() => fileRef.current?.click()} aria-label="צרף קובץ" title="צרף קובץ">
                <Icon name="upload" size={16} />
              </button>
              <TextInput value={reply} onChange={(e) => setReply(e.target.value)} placeholder="כתוב תגובה…" style={{ flex: 1 }}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }} />
              <button type="button" className="btn primary" onClick={send} disabled={sending} aria-label="שלח"><Icon name="send" size={14} /></button>
            </div>
            {files.length > 0 && (
              <div style={{ padding: '0 var(--pad-card) var(--pad-row)', fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                {files.map((f) => f.name).join(', ')}
              </div>
            )}
          </>
        )}
      </Card>
  );

  return (
    <>
      <TwoPane
        list={listPane}
        detail={threadPane}
        hasSelection={active != null}
        onBack={() => setActive(null)}
        railWidth={340}
        backLabel="חזרה לפניות"
      />
      {newOpen && (
        <NewTicketModal api={api} audiences={canOpen} onClose={() => setNewOpen(false)} onCreated={() => { setNewOpen(false); void load(); }} />
      )}
    </>
  );
}

function NewTicketModal({ api, audiences, onClose, onCreated }: {
  api: TicketApi;
  audiences: { value: 'internal' | 'platform'; label: string }[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [audience, setAudience] = useState<'internal' | 'platform'>(audiences[0]?.value ?? 'platform');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState('normal');
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!subject.trim() || !body.trim()) {
      toast('יש למלא נושא ותיאור', 'danger');
      return;
    }
    setSaving(true);
    try {
      await api.create({ audience, subject, body, priority, files });
      toast('הפנייה נשלחה', 'ok');
      onCreated();
    } catch {
      toast('שגיאה בפתיחת פנייה', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title="פנייה חדשה"
      footer={<><button type="button" className="btn" onClick={onClose} disabled={saving}>ביטול</button><button type="button" className="btn primary" onClick={save} disabled={saving}>{saving ? 'שולח…' : 'שלח'}</button></>}>
      <div className="stack">
        {audiences.length > 1 && (
          <FormField label="פנייה אל">
            <select value={audience} onChange={(e) => setAudience(e.target.value as 'internal' | 'platform')} style={selectCss}>
              {audiences.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </FormField>
        )}
        <FormField label="נושא" required><TextInput value={subject} onChange={(e) => setSubject(e.target.value)} /></FormField>
        <FormField label="עדיפות">
          <select value={priority} onChange={(e) => setPriority(e.target.value)} style={selectCss}>
            {Object.entries(PRIORITY_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </FormField>
        <FormField label="תיאור" required>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5}
            style={{ width: '100%', padding: 10, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)', resize: 'vertical', fontFamily: 'inherit' }} />
        </FormField>
        <FormField label="קבצים מצורפים">
          <input type="file" multiple onChange={(e) => setFiles(Array.from(e.target.files ?? []))} />
        </FormField>
      </div>
    </Modal>
  );
}

const selectCss = { padding: '6px 10px', border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)' };
